// Demo-mode controller. When the app is opened at /demo with a valid key, the whole
// PHR data layer is served from an in-memory synthetic dataset instead of Firebase —
// no login, no PHI. Edits mutate the in-memory copy for the session (reset on reload).
// The app's data functions (phr.ts, imports.ts, …) check isDemo() and route here;
// stores.ts calls startDemo() to seed the shell.
//
// Two kinds of key open the demo:
//   • a legacy 6-character tester code (CODES below) → the built-in family record
//     (demoData.ts: Alex Ellis managing spouse, parent and child);
//   • a 12-character access key issued in the admin console → /api/demo/open
//     validates it server-side and returns a record in the app's own schema (today:
//     Arjun Nair, 12 years of type 2 diabetes; see functions/demo). That record
//     replaces the family dataset for the session (setDemoDataset).
import { writable } from 'svelte/store';
import type { PHR } from './types';
import type { Profile } from './phr';
import type { ChatTurn } from './chat';
import { demoProfiles, demoPHRs, DEMO_UID } from './demoData';
import { resolveLandmark } from './anatomyLandmarks';

export { demoProfiles, DEMO_UID };
export const demoActive = writable(false);
// Live per-key chat token budget, shown in the demo footer. Updated from /api/chat
// responses + a fetch on start. Reset is server-side (admin zeroes the usage doc).
export const demoUsage = writable<{ used: number; cap: number }>({ used: 0, cap: 100000 });

let _demo = false;
export const isDemo = (): boolean => _demo;
export function setDemo(on: boolean): void { _demo = on; demoActive.set(on); }

// Valid 6-character test keys (case-insensitive). EMPWR1 is kept for early testers;
// the rest are randomly generated. Hand these out to testers; each key is that
// tester's identity for activity logging + the per-key chat token budget (see the
// backend metering in server/index.js — KEEP THIS LIST IN SYNC there).
const CODES = [
  'EMPWR1',
  'CVUKVZ', 'EBLQEY', 'DLLSBV', 'HGVWVC', 'TRFY4S', 'EGJHAM',
  '9A5X8Q', 'SVWDRU', 'B7MX85', 'YJWRBW', 'U3W5FV', 'ZX9FUJ',
];
export function isDemoRoute(): boolean { try { return /^\/demo(\/|$)/.test(location.pathname); } catch { return false; } }
export function validCode(c: string): boolean { return CODES.includes((c || '').trim().toUpperCase()); }

// ── Which record the demo runs on (family by default; swappable per key) ──────
export interface DemoUser { uid: string; email: string; displayName: string }
export interface DemoPrefs { dashLabs?: string[] }
export interface DemoDataset { profiles: Profile[]; phrs: Record<string, PHR>; user?: DemoUser; prefs?: DemoPrefs; label?: string }
const FAMILY_USER: DemoUser = { uid: DEMO_UID, email: 'alex.ellis@demo.empowered', displayName: 'Alex Ellis' };
let _profiles: Profile[] = demoProfiles;
let _phrs: Record<string, PHR> = demoPHRs;
let _user: DemoUser = FAMILY_USER;
let _prefs: DemoPrefs = {};
let _label = 'Family demo';

const clone = <T>(x: T): T => (typeof structuredClone === 'function' ? structuredClone(x) : JSON.parse(JSON.stringify(x)));
// Firestore records get their avatar position + system from anatomyLandmarks in
// loadPHR; demo records arrive as plain JSON, so resolve them here the same way
// (the description decides which layer — eyes in nerves, kidney in organs, …).
function placed(phrs: Record<string, PHR>): Record<string, PHR> {
  const out = clone(phrs);
  for (const p of Object.values(out)) p.conditions = p.conditions.map((c) => { const lm = resolveLandmark(c.description || ''); return lm ? { ...c, pos: c.pos ?? lm.pos, system: c.system ?? lm.system } : c; });
  return out;
}
// In-memory, mutable copy of the dataset (tester edits persist for the session).
let live: Record<string, PHR> = placed(_phrs);

/** Swap the record the demo runs on (before startDemo). */
export function setDemoDataset(d: DemoDataset): void {
  _profiles = d.profiles; _phrs = d.phrs; _user = d.user ?? FAMILY_USER; _prefs = d.prefs ?? {}; _label = d.label ?? 'Demo';
  live = placed(_phrs);
}
export function resetDemo(): void { live = placed(_phrs); }
export const demoUser = (): DemoUser => _user;
export const demoPrefs = (): DemoPrefs => _prefs;
export const demoLabel = (): string => _label;
export function demoLoadPHR(pid: string): PHR { return clone(live[pid] ?? live[_profiles[0]?.id]); }
export function demoProfilesList(): Profile[] { return _profiles; }

// ── Tester identity (the entered key) + backend telemetry / metered chat ─────────
// The key the tester entered is their identity for activity logging and the per-key
// chat token budget (both enforced server-side in mpwrd-api).
const normKeyLocal = (k: string): string => (k || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
let enteredKey = '';
export function setDemoKey(k: string): void { enteredKey = normKeyLocal(k); }
export function demoKeyValue(): string { return enteredKey; }

/** Console-issued access key → /api/demo/open (functions/demo.mjs). On success the
 *  returned record becomes the demo dataset. Throws with a human message otherwise. */
export async function openWithAccessKey(key: string): Promise<void> {
  const k = normKeyLocal(key);
  const res = await fetch('/api/demo/open', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ key: k }) });
  const text = await res.text();
  let data: any;
  try { data = JSON.parse(text); } catch { data = {}; }
  if (!res.ok || !data.ok) throw new Error(data.error || (res.status === 404 ? 'The demo service is not reachable from this address.' : `Could not open the demo (${res.status}).`));
  if (!data.phr || !data.profile) throw new Error('This key opens a record this app version cannot display yet.');
  const profile = data.profile as Profile;
  setDemoDataset({ profiles: [profile], phrs: { [profile.id]: data.phr as PHR }, user: data.user, prefs: data.prefs, label: data.access?.label || 'Physician demo' });
  setDemoKey(k);
}

/** Fire-and-forget activity event for the admin portal (keyed by the tester's key). */
export function logDemoEvent(event: string, detail = ''): void {
  if (!enteredKey) return;
  try {
    fetch('/api/telemetry', {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-demo-key': enteredKey },
      body: JSON.stringify({ event, detail }),
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* telemetry must never break the app */ }
}

/** Metered demo chat → mpwrd-api /api/chat (per-key token budget). Conversational only
 *  (the record-appending assistant runs on susruta, which the demo doesn't use). */
export async function demoChat(messages: ChatTurn[], context: string): Promise<string> {
  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-demo-key': enteredKey },
    body: JSON.stringify({ messages, context }),
  });
  const data = await res.json().catch(() => ({} as any));
  if (res.status === 429) { demoUsage.update((u) => ({ ...u, used: u.cap })); throw new Error(data.error || 'You’ve reached the demo assistant limit.'); }
  if (!res.ok || !data.ok) throw new Error(data.error || `Assistant error (${res.status})`);
  if (data.usage) demoUsage.set({ used: data.usage.used || 0, cap: data.usage.cap || 100000 });
  return String(data.text || '');
}

/** Pull the current per-key usage (called on demo start; the footer shows it live). */
export async function fetchDemoUsage(): Promise<void> {
  if (!enteredKey) return;
  try {
    const res = await fetch('/api/demo/usage', { headers: { 'x-demo-key': enteredKey } });
    const data = await res.json().catch(() => ({} as any));
    if (res.ok && data.ok) demoUsage.set({ used: data.used || 0, cap: data.cap || 100000 });
  } catch (_) { /* non-fatal */ }
}

let _seq = 1;
const newId = (): string => `demo-${_seq++}-${(performance.now() | 0).toString(36)}`;

// Raw reading-doc field → assembled VitalSeries {key,label,unit} (mirrors VITAL_DEFS in phr.ts).
const VITAL_META: Record<string, { key: string; label: string; unit: string }> = {
  temperature: { key: 'temperature', label: 'Temperature', unit: '°F' },
  heartRate:   { key: 'hr',          label: 'Heart rate',  unit: 'bpm' },
  systolic:    { key: 'bpSystolic',  label: 'Systolic',    unit: 'mmHg' },
  diastolic:   { key: 'bpDiastolic', label: 'Diastolic',   unit: 'mmHg' },
  respiration: { key: 'rr',          label: 'Resp. rate',  unit: '/min' },
  spo2:        { key: 'spo2',        label: 'SpO₂',        unit: '%' },
  height:      { key: 'height',      label: 'Height',      unit: 'in' },
  weight:      { key: 'weight',      label: 'Weight',      unit: 'lb' },
};

/** Append a record to the in-memory profile; returns a generated id. */
export function demoAdd(pid: string, kind: string, data: Record<string, any>): string {
  const p = live[pid]; if (!p) return '';
  const id = newId();
  if (kind === 'labs') p.labs.unshift({ code: data.code || 'LAB', name: data.name || data.code || 'Panel', date: data.date || '', source: data.provider || 'self', results: data.results || [], _id: id });
  else if (kind === 'conditions') p.conditions.unshift({ code: data.code || data.description || '', description: data.description || '', status: data.status || 'Active', since: data.since || '', provider: data.provider || 'self', _id: id });
  else if (kind === 'medications') p.medications.unshift({ name: data.name || '', dose: data.dose, route: data.route, frequency: data.frequency, indication: data.indication, source: data.provider || 'self', since: data.since, until: data.until, _id: id });
  else if (kind === 'imaging') p.imaging.unshift({ id, modality: data.modality || 'xray', bodyPart: data.bodyPart || '', date: data.date || '', source: data.provider || 'self', impression: data.impression || '', radiologist: data.radiologist || '' });
  else if (kind === 'procedures') p.procedures.unshift({ id, kind: data.kind || 'other', name: data.name || '', date: data.date || '', source: data.provider || 'self', operator: data.operator || '', status: data.status || 'Completed', summary: data.summary || '' });
  else if (kind === 'visits') p.wrappers.unshift({ id, kind: data.kind || 'outpatient', title: data.title || '', date: data.date || '', endDate: data.endDate, source: data.source || data.provider || 'self', summary: data.summary || '', archived: false, attachments: [] });
  else if (kind === 'notes') p.notes.unshift({ id, date: data.date || '', title: data.title || '', body: data.body || '', author: data.author || 'self' });
  else if (kind === 'vitals') {
    for (const [field, val] of Object.entries(data)) {
      if (val == null || val === '') continue;
      const meta = VITAL_META[field]; if (!meta) continue;
      const num = Number(val); if (!Number.isFinite(num)) continue;
      let s = p.vitals.find((x) => x.key === (meta.key as any));
      if (!s) { s = { key: meta.key as any, label: meta.label, unit: meta.unit, samples: [] }; p.vitals.push(s); }
      s.samples.push({ date: data.date || '', time: data.time, value: num, source: data.provider || 'self', _id: id });
    }
  }
  logDemoEvent('add_record', kind);
  return id;
}

const ARRAY: Record<string, keyof PHR> = { labs: 'labs', conditions: 'conditions', medications: 'medications', imaging: 'imaging', procedures: 'procedures', visits: 'wrappers', notes: 'notes' };

export function demoUpdate(pid: string, kind: string, id: string, fields: Record<string, any>): void {
  const p = live[pid]; if (!p) return;
  const key = ARRAY[kind]; if (!key) return;
  const arr = p[key] as any[];
  const item = arr.find((x) => x._id === id || x.id === id);
  if (item) Object.assign(item, fields);
}

export function demoDelete(pid: string, kind: string, id: string): void {
  const p = live[pid]; if (!p) return;
  const key = ARRAY[kind]; if (!key) return;
  (p as any)[key] = (p[key] as any[]).filter((x) => x._id !== id && x.id !== id);
}
